import { ThemeEvents } from '@theme/events';

/**
 * Toggle PDP restock notify vs add-to-cart when the selected color variant is out of stock.
 * @param {Element} block
 * @param {boolean} available
 * @param {Document | Element | null} [html]
 */
function syncBuyButtonsBlock(block, available, html) {
  if (block.dataset.customOrder === 'true') return;

  const restock = block.querySelector('[data-vidtout-restock-notify]');
  const cartActions = block.querySelector('[data-vidtout-cart-actions]');
  const quantityActions = block.querySelector('[data-vidtout-quantity-actions]');
  const volumePricing = block.querySelector('[ref="volumePricing"]');
  if (!restock) return;

  const showRestock = available !== true;
  restock.hidden = !showRestock;
  if (cartActions instanceof HTMLElement) {
    cartActions.hidden = showRestock;
  }
  if (quantityActions instanceof HTMLElement) {
    quantityActions.hidden = showRestock;
  }
  if (volumePricing instanceof HTMLElement) {
    volumePricing.hidden = showRestock;
  }

  if (!showRestock || !html) return;

  const newRestock = html.querySelector('[data-vidtout-restock-notify]');
  if (!newRestock) return;

  const tagsInput = restock.querySelector('[data-vidtout-notify-tags]');
  const newTags = newRestock.querySelector('[data-vidtout-notify-tags]');

  if (tagsInput instanceof HTMLInputElement && newTags instanceof HTMLInputElement) {
    tagsInput.value = newTags.value;
  }
}

/**
 * Infer whether the current variant is unavailable (single-variant PDPs have no variant picker).
 * @param {Element} block
 * @returns {boolean | null}
 */
function isBlockVariantUnavailable(block) {
  const addToCartButton = block.querySelector('.add-to-cart-button, [ref="addToCartButton"]');
  if (addToCartButton instanceof HTMLButtonElement) {
    return addToCartButton.disabled;
  }

  const restock = block.querySelector('[data-vidtout-restock-notify]');
  if (restock instanceof HTMLElement && !restock.hidden) {
    return true;
  }

  return null;
}

/**
 * Scroll to and reveal the restock form when arriving from a product card notify link.
 */
function focusRestockNotifyFromHash() {
  if (window.location.hash !== '#restock-notify') return;

  const restock = document.getElementById('restock-notify');
  if (!(restock instanceof HTMLElement)) return;

  const block = restock.closest('.buy-buttons-block');
  if (block instanceof Element) {
    syncBuyButtonsBlock(block, false, null);
  } else {
    restock.hidden = false;
  }

  window.requestAnimationFrame(() => {
    restock.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const emailInput = restock.querySelector('input[type="email"]');
    if (emailInput instanceof HTMLInputElement) {
      emailInput.focus({ preventScroll: true });
    }
  });
}

function syncAllBuyButtonsFromPageState() {
  document.querySelectorAll('.buy-buttons-block').forEach((block) => {
    const unavailable = isBlockVariantUnavailable(block);
    if (unavailable === null) return;
    syncBuyButtonsBlock(block, unavailable ? false : true, null);
  });
}

function initRestockNotify() {
  syncAllBuyButtonsFromPageState();
  focusRestockNotifyFromHash();
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const { resource, data } = event.detail ?? {};
  if (!data?.html) return;

  document.querySelectorAll('.buy-buttons-block').forEach((block) => {
    const formComponent = block.querySelector('product-form-component');
    if (!formComponent) return;

    const eventProductId = data.newProduct?.id ?? data.productId;
    if (eventProductId && formComponent.dataset.productId !== eventProductId) return;

    syncBuyButtonsBlock(block, resource?.available === true, data.html);
  });

  focusRestockNotifyFromHash();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRestockNotify);
} else {
  initRestockNotify();
}

window.addEventListener('hashchange', focusRestockNotifyFromHash);
